/**
 * format.js — Display formatting helpers.
 *
 * All currency uses the en-IN locale so amounts group the Indian way
 * (₹1,19,900 rather than ₹119,900), which is what an Indian shopper expects to
 * see and is easy to get subtly wrong by hand.
 */

const inrWhole = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const inrPaise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const plainNumber = new Intl.NumberFormat('en-IN');

/**
 * Format an amount as rupees.
 * @param {number} value
 * @param {object} [options]
 * @param {boolean} [options.paise=false] Show two decimal places.
 */
export function currency(value, { paise = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return (paise ? inrPaise : inrWhole).format(Number(value));
}

/** Compact form for dense contexts: ₹1.2L, ₹2.4Cr. */
export function currencyCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(n % 10_000_000 === 0 ? 0 : 2)}Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(n % 100_000 === 0 ? 0 : 2)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return currency(n);
}

export function number(value) {
  return Number.isFinite(Number(value)) ? plainNumber.format(Number(value)) : '—';
}

/** Render an interest rate, collapsing a zero rate to the marketing term. */
export function rate(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0%';
  return `${n % 1 === 0 ? n : n.toFixed(2).replace(/0$/, '')}%`;
}

/** "12 months", "1 month". */
export function tenure(months) {
  const n = Number(months);
  if (!Number.isFinite(n)) return '—';
  return `${n} ${n === 1 ? 'month' : 'months'}`;
}

/** Short date for tables and charts: "3 Sep". */
export function shortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Full date and time for lead records. */
export function dateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function percent(value, { decimals = 1 } = {}) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(decimals).replace(/\.0$/, '')}%` : '—';
}

/** Join class names, dropping falsy values so conditionals read cleanly. */
export function cx(...values) {
  return values.filter(Boolean).join(' ');
}
